import { useCallback, useEffect, useMemo, useState } from "react";
import { useDogs } from "@/context/DogsContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { SectionTitle } from "@/components/app/Bits";
import { mergeCustomActivities } from "@/lib/activities";
import CustomActivitiesDialog from "@/components/app/CustomActivitiesDialog";
import { Bell, BellOff, Mail, Trash2, Plus, Send, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";

const FREQUENCY_TABS = [
  { value: "daily", label: "Daily", description: "Sent every day at the time you pick." },
  { value: "weekly", label: "Weekly", description: "Sent on the days of the week you pick." },
  { value: "monthly", label: "Monthly", description: "Sent on the same date each month." },
  { value: "custom", label: "Custom", description: "Pick any frequency, including one-time." },
];

const CUSTOM_FREQUENCY_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "once", label: "One-time" },
];

// 0=Mon..6=Sun (Python weekday() convention)
const DAYS = [
  { v: 0, l: "M" }, { v: 1, l: "T" }, { v: 2, l: "W" },
  { v: 3, l: "T" }, { v: 4, l: "F" }, { v: 5, l: "S" }, { v: 6, l: "S" },
];

function emptyForm(activeDog, frequency) {
  return {
    dog_id: activeDog?.id || null,
    kind: "custom",
    prompt_key: null,
    title: "",
    notes: "",
    frequency,
    days_of_week: [0, 1, 2, 3, 4],
    time_of_day: "08:00",
    tz_offset_minutes: new Date().getTimezoneOffset(),
    run_once_date: format(new Date(), "yyyy-MM-dd"),
    enabled: true,
  };
}

export default function RemindersPage() {
  const { activeDog, dogs } = useDogs();
  const [status, setStatus] = useState({ enabled: true });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("daily");
  const [forms, setForms] = useState({
    daily: emptyForm(activeDog, "daily"),
    weekly: emptyForm(activeDog, "weekly"),
    monthly: emptyForm(activeDog, "monthly"),
    custom: emptyForm(activeDog, "daily"),
  });
  const [saving, setSaving] = useState(false);

  // Custom activities are per-dog. On this page they follow the currently
  // selected dog in each form row (falls back to `activeDog`) so the dropdown
  // reflects that dog's kennel routines.
  const [customActivities, setCustomActivities] = useState([]);
  const [pinnedLabels, setPinnedLabels] = useState([]);
  const [manageOpen, setManageOpen] = useState(false);
  const formDogId = forms[tab]?.dog_id || activeDog?.id || null;
  const { groups: mergedGroups } = useMemo(
    () => mergeCustomActivities(customActivities, pinnedLabels),
    [customActivities, pinnedLabels],
  );

  const reloadCustomActivities = useCallback(async () => {
    if (!formDogId) { setCustomActivities([]); setPinnedLabels([]); return; }
    try {
      const [customRes, pinnedRes] = await Promise.all([
        api.get(`/dogs/${formDogId}/custom-activities`),
        api.get(`/dogs/${formDogId}/pinned-activities`),
      ]);
      setCustomActivities(customRes.data.items || []);
      setPinnedLabels((pinnedRes.data.items || []).map((p) => p.label));
    } catch {
      setCustomActivities([]);
      setPinnedLabels([]);
    }
  }, [formDogId]);

  useEffect(() => { reloadCustomActivities(); }, [reloadCustomActivities]);

  const load = async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([api.get("/reminders/status"), api.get("/reminders")]);
      setStatus(s.data);
      setItems(r.data.items || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    setForms((cur) => {
      const next = { ...cur };
      ["daily", "weekly", "monthly", "custom"].forEach((k) => {
        next[k] = { ...next[k], dog_id: activeDog?.id || null };
      });
      return next;
    });
  }, [activeDog]);

  const updateForm = (freq, patch) => setForms((cur) => ({ ...cur, [freq]: { ...cur[freq], ...patch } }));

  const toggleDay = (freq, d) => {
    setForms((cur) => {
      const f = cur[freq];
      const has = f.days_of_week.includes(d);
      const days = has ? f.days_of_week.filter((x) => x !== d) : [...f.days_of_week, d].sort();
      return { ...cur, [freq]: { ...f, days_of_week: days } };
    });
  };

  const submit = async (tabKey) => {
    const f = forms[tabKey];
    if (!f.title?.trim()) { toast.error("Pick an activity"); return; }
    if (f.frequency === "weekly" && (f.days_of_week || []).length === 0) {
      toast.error("Pick at least one day of the week"); return;
    }
    setSaving(true);
    try {
      await api.post("/reminders", { ...f, tz_offset_minutes: new Date().getTimezoneOffset() });
      toast.success(`${f.frequency === "once" ? "One-time" : f.frequency[0].toUpperCase() + f.frequency.slice(1)} reminder added`);
      await load();
      updateForm(tabKey, { title: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this reminder?")) return;
    try {
      await api.delete(`/reminders/${id}`);
      toast.success("Reminder removed");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  const toggleEnabled = async (r) => {
    try {
      await api.put(`/reminders/${r.id}`, { ...r, enabled: !r.enabled, tz_offset_minutes: new Date().getTimezoneOffset() });
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
  };

  const sendNow = async (r) => {
    try {
      await api.post(`/reminders/${r.id}/send-now`);
      toast.success("Sent!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed");
    }
  };

  const dogName = (id) => dogs.find((d) => d.id === id)?.name || "Any dog";
  const fmtNext = (iso) => {
    if (!iso) return "—";
    try { return format(parseISO(iso), "EEE, MMM d 'at' h:mm a"); } catch (e) { return iso; }
  };

  const sectionMeta = FREQUENCY_TABS.find((t) => t.value === tab) || FREQUENCY_TABS[0];
  const tabReminders = items.filter((r) => r.frequency === (tab === "custom" ? forms.custom.frequency : tab));

  return (
    <div className="space-y-8" data-testid="reminders-page">
      <SectionTitle overline="Email reminders" title="Activity prompts in your inbox" />

      {!status.enabled && (
        <Card className="border-amber-300 bg-amber-50 shadow-none rounded-lg" data-testid="reminders-disabled-banner">
          <CardContent className="p-5 flex items-start gap-3">
            <AlertCircle className="text-amber-600 shrink-0 mt-0.5" size={18} />
            <div className="text-sm">
              <p className="font-medium text-amber-900">Email sending is not configured yet.</p>
              <p className="text-amber-800 mt-1">
                You can still create reminders. To deliver emails, set a <code className="bg-amber-100 px-1.5 py-0.5 rounded">RESEND_API_KEY</code> in the backend env and restart.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border overflow-x-auto" data-testid="reminders-tabs">
        {FREQUENCY_TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            onClick={() => setTab(t.value)}
            data-testid={`reminders-tab-${t.value}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              tab === t.value ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Add form */}
      <Card className="border-border shadow-none rounded-lg" data-testid={`reminders-form-${tab}`}>
        <CardContent className="p-6 sm:p-8 space-y-5">
          <div>
            <h3 className="font-display text-xl font-bold tracking-tight">Add a {tab === "custom" ? "custom" : tab} reminder</h3>
            <p className="text-sm text-muted-foreground mt-1">{sectionMeta.description}</p>
          </div>

          {/* Activity picker */}
          <div>
            <div className="flex items-center justify-between gap-2">
              <Label>Activity</Label>
              <button
                type="button"
                onClick={() => setManageOpen(true)}
                disabled={!formDogId}
                className="text-[11px] uppercase tracking-wider text-primary hover:underline disabled:text-muted-foreground disabled:no-underline"
                data-testid={`reminders-manage-activities-${tab}`}
              >
                Manage
              </button>
            </div>
            <select
              value={forms[tab].title}
              onChange={(e) => updateForm(tab, { title: e.target.value })}
              className="mt-1.5 w-full max-w-md border border-input rounded-md bg-card px-3 py-2 text-sm"
              data-testid={`reminder-activity-${tab}`}
            >
              <option value="">Select an activity…</option>
              {mergedGroups.map((g) => (
                <optgroup key={g.group} label={g.group}>
                  {g.items.map((a) => <option key={a.label} value={a.label}>{a.label}</option>)}
                </optgroup>
              ))}
            </select>
          </div>

          {/* Dog */}
          <div>
            <Label>Dog (optional)</Label>
            <select
              value={forms[tab].dog_id || ""}
              onChange={(e) => updateForm(tab, { dog_id: e.target.value || null })}
              className="mt-1.5 w-full max-w-md border border-input rounded-md bg-card px-3 py-2 text-sm"
              data-testid={`reminder-dog-${tab}`}
            >
              <option value="">Any dog</option>
              {dogs.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>

          {/* Custom-only: frequency picker */}
          {tab === "custom" && (
            <div>
              <Label>Frequency</Label>
              <select
                value={forms.custom.frequency}
                onChange={(e) => updateForm("custom", { frequency: e.target.value })}
                className="mt-1.5 w-full max-w-md border border-input rounded-md bg-card px-3 py-2 text-sm"
                data-testid="reminder-custom-frequency"
              >
                {CUSTOM_FREQUENCY_OPTIONS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
            </div>
          )}

          {/* Weekly-only: day chips (also for custom + weekly) */}
          {(tab === "weekly" || (tab === "custom" && forms.custom.frequency === "weekly")) && (
            <div>
              <Label>Days of week</Label>
              <div className="mt-1.5 flex gap-1.5">
                {DAYS.map((d) => {
                  const active = forms[tab].days_of_week.includes(d.v);
                  return (
                    <button
                      key={d.v}
                      type="button"
                      onClick={() => toggleDay(tab, d.v)}
                      className={`h-9 w-9 rounded-md border text-sm font-medium transition-colors ${
                        active ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-muted-foreground hover:text-foreground"
                      }`}
                      data-testid={`reminder-day-${tab}-${d.v}`}
                    >
                      {d.l}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Once-only: date */}
          {tab === "custom" && forms.custom.frequency === "once" && (
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={forms.custom.run_once_date}
                onChange={(e) => updateForm("custom", { run_once_date: e.target.value })}
                className="mt-1.5 max-w-md"
                data-testid="reminder-once-date"
              />
            </div>
          )}

          {/* Time */}
          <div>
            <Label>Time of day</Label>
            <Input
              type="time"
              value={forms[tab].time_of_day}
              onChange={(e) => updateForm(tab, { time_of_day: e.target.value })}
              className="mt-1.5 max-w-[160px]"
              data-testid={`reminder-time-${tab}`}
            />
          </div>

          <div>
            <Button
              type="button"
              onClick={() => submit(tab)}
              disabled={saving}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid={`reminder-submit-${tab}`}
            >
              <Plus size={14} className="mr-1.5" />
              {saving ? "Saving…" : "Add reminder"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Existing reminders for this tab */}
      <div>
        <h3 className="font-display text-lg font-bold tracking-tight mb-3">
          Your {tab === "custom" ? "custom" : tab} reminders
        </h3>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : tabReminders.length === 0 ? (
          <Card className="border-dashed border-border shadow-none rounded-lg">
            <CardContent className="p-8 text-center text-sm text-muted-foreground">
              No {tab === "custom" ? "custom" : tab} reminders yet. Add one above.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2" data-testid={`reminders-list-${tab}`}>
            {tabReminders.map((r) => (
              <Card key={r.id} className="border-border shadow-none rounded-lg" data-testid={`reminder-${r.id}`}>
                <CardContent className="p-4 flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    {r.enabled ? <Bell size={16} className="text-primary shrink-0" /> : <BellOff size={16} className="text-muted-foreground shrink-0" />}
                    <div className="min-w-0">
                      <p className="font-medium truncate">{r.title || r.prompt_key}</p>
                      <p className="text-xs text-muted-foreground">
                        {dogName(r.dog_id)} · {r.time_of_day} · next {fmtNext(r.next_run_at)}
                      </p>
                    </div>
                  </div>
                  <Switch checked={r.enabled !== false} onCheckedChange={() => toggleEnabled(r)} aria-label="Toggle reminder" />
                  <Button variant="outline" size="sm" onClick={() => sendNow(r)} data-testid={`reminder-send-${r.id}`}>
                    <Send size={14} className="mr-1.5" /> Send test
                  </Button>
                  <button
                    type="button"
                    onClick={() => remove(r.id)}
                    className="p-1.5 rounded text-muted-foreground hover:bg-destructive hover:text-destructive-foreground transition-colors"
                    data-testid={`reminder-delete-${r.id}`}
                    aria-label="Delete reminder"
                  >
                    <Trash2 size={14} />
                  </button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Card className="border-border shadow-none rounded-lg bg-secondary/30">
        <CardContent className="p-5 text-sm text-muted-foreground flex items-start gap-3">
          <Mail size={16} className="shrink-0 mt-0.5" />
          <p>
            Reminders are sent to the email on your account. Want to change the destination? Update your email in Settings.
          </p>
        </CardContent>
      </Card>

      {formDogId ? (
        <CustomActivitiesDialog
          open={manageOpen}
          onOpenChange={setManageOpen}
          dogId={formDogId}
          dogName={(dogs.find((d) => d.id === formDogId) || {}).name}
          onChanged={reloadCustomActivities}
        />
      ) : null}
    </div>
  );
}
